import type { DeviceModel, PowerInput, PowerOutput } from '../../types/deviceModel';
import { defaultPoeIn, defaultPoeOut } from '../../engine/networkLogic/electricalDraft';
import { createPortRole } from '../../engine/networkLogic/portRoles';

export interface ElectricalPreset {
  id: string;
  group: 'input' | 'output' | 'role';
  label: string;
}

export const ELECTRICAL_PRESETS: ElectricalPreset[] = [
  { id: 'poe-out-afat', group: 'role', label: 'PoE-out 802.3af/at' },
  { id: 'poe-out-bt', group: 'role', label: 'PoE-out 802.3bt' },
  { id: 'poe-in-af', group: 'role', label: 'PoE-in 802.3af' },
  { id: 'poe-in-24', group: 'role', label: 'PoE-in 24 V passive' },
  { id: 'dc-48', group: 'input', label: 'DC input 48 V' },
  { id: 'dc-24', group: 'input', label: 'DC input 24 V' },
  { id: 'ac-in', group: 'input', label: 'AC input 100–240 V' },
  { id: 'pv', group: 'input', label: 'PV / MPPT' },
  { id: 'battery', group: 'input', label: 'Battery 40–60 V' },
  { id: 'ac-out', group: 'output', label: 'AC output' },
  { id: 'dc-out', group: 'output', label: 'DC output' },
  { id: 'custom-input', group: 'input', label: 'Custom power input…' },
  { id: 'custom-output', group: 'output', label: 'Custom power output…' },
  { id: 'custom-role', group: 'role', label: 'Custom PoE rule…' },
];

function uid(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 999)}`;
}

export function applyElectricalPreset(model: DeviceModel, presetId: string): DeviceModel {
  const roles = model.portRoles || [];
  if (presetId === 'poe-out-afat' || presetId === 'custom-role') {
    const spec = defaultPoeOut(['802.3af', '802.3at']);
    if (presetId === 'custom-role') spec.standards = ['802.3af'];
    const role = createPortRole('poe-out', spec, roles);
    return { ...model, portRoles: [...roles, role] };
  }
  if (presetId === 'poe-out-bt') {
    const role = createPortRole('poe-out', defaultPoeOut(['802.3bt']), roles);
    role.poeOut = { ...role.poeOut!, perPortMaxWatts: 60, standards: ['802.3bt'] };
    return { ...model, portRoles: [...roles, role] };
  }
  if (presetId === 'poe-in-af') {
    const role = createPortRole('poe-in', defaultPoeIn(), roles);
    return { ...model, portRoles: [...roles, role] };
  }
  if (presetId === 'poe-in-24') {
    const role = createPortRole('poe-in', {
      standards: ['passive-24'],
      voltageMin: 20,
      voltageMax: 28,
      maxWatts: 24,
    }, roles);
    return { ...model, portRoles: [...roles, role] };
  }
  if (presetId === 'dc-48' || presetId === 'dc-24') {
    const v = presetId === 'dc-48' ? 48 : 24;
    const input: PowerInput = {
      id: uid('dc-jack'),
      kind: 'dc-jack',
      enabled: true,
      voltageMin: v,
      voltageMax: v,
      maxWatts: v === 48 ? 150 : 30,
      capacityLimit: 'watts',
    };
    return { ...model, powerInputs: [...model.powerInputs, input] };
  }
  if (presetId === 'ac-in' || presetId === 'custom-input') {
    const input: PowerInput = {
      id: uid('ac'),
      kind: 'ac',
      enabled: true,
      voltageMin: 100,
      voltageMax: 240,
      maxWatts: 200,
      capacityLimit: 'watts',
    };
    return { ...model, powerInputs: [...model.powerInputs, input] };
  }
  if (presetId === 'pv') {
    const id = uid('pv');
    const input: PowerInput = {
      id,
      kind: 'pv',
      enabled: true,
      mpptId: id,
      voltageMax: 500,
      mpptVoltageMin: 150,
      mpptVoltageMax: 425,
      maxWatts: 3000,
      capacityLimit: 'watts',
    };
    return { ...model, powerInputs: [...model.powerInputs, input] };
  }
  if (presetId === 'battery') {
    const input: PowerInput = {
      id: uid('battery'),
      kind: 'battery',
      enabled: true,
      voltageMin: 40,
      voltageMax: 60,
      maxAmps: 100,
      capacityLimit: 'amps',
    };
    return { ...model, powerInputs: [...model.powerInputs, input] };
  }
  if (presetId === 'ac-out' || presetId === 'custom-output') {
    const output: PowerOutput = {
      id: uid('ac-out'),
      kind: 'ac',
      enabled: true,
      voltageMin: 220,
      voltageMax: 240,
      maxWatts: 5000,
      capacityLimit: 'watts',
    };
    return { ...model, powerOutputs: [...(model.powerOutputs || []), output] };
  }
  if (presetId === 'dc-out') {
    const output: PowerOutput = {
      id: uid('dc-out'),
      kind: 'dc',
      enabled: true,
      voltageMin: 24,
      voltageMax: 24,
      maxWatts: 60,
      capacityLimit: 'watts',
    };
    return { ...model, powerOutputs: [...(model.powerOutputs || []), output] };
  }
  return model;
}
